/*
 * Purpose: Mission knowledge library — AI request page + universal documents (all missions)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 98
 */
import { useMemo, useState } from 'react';
import { BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useMissionKnowledgeStore } from '@/stores/missionKnowledgeStore';
import { MissionRequestPage } from '@/components/mission/request-fields/MissionRequestPage';
import { MissionDocumentsLibrary } from '@/components/mission/MissionDocumentsLibrary';
import { MissionIntegrationDiscoveryPanel } from '@/components/mission/MissionIntegrationDiscoveryPanel';
import { resolveKnowledgeRequests } from '@/lib/missionKnowledgeForm';
import type { MissionKnowledgeRequest } from '@/services/mission-knowledge/types';
import { cn } from '@/lib/utils';

function MissionScopeBadge() {
  const missionSessionId = useMissionKnowledgeStore((s) => s.missionSessionId);
  const ragAvailable = useMissionKnowledgeStore((s) => s.ragAvailable);
  const lastSyncAt = useMissionKnowledgeStore((s) => s.lastSyncAt);

  const shortId = missionSessionId ? missionSessionId.slice(-8) : '';

  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {shortId && (
        <Badge variant="outline" className="text-[10px] font-mono">
          Mission scope · …{shortId}
        </Badge>
      )}
      {ragAvailable === true && (
        <Badge variant="secondary" className="text-[10px]">
          Synced to pgvector
        </Badge>
      )}
      {ragAvailable === false && lastSyncAt && (
        <Badge variant="outline" className="text-[10px]">
          Local until RAG configured
        </Badge>
      )}
    </div>
  );
}

interface MissionKnowledgePanelProps {
  knowledgeGaps: string[];
  rawOutcome: string;
  interpretedGoal?: string;
  allGaps?: string[];
  knowledgeRequests?: MissionKnowledgeRequest[];
  className?: string;
}

export function MissionKnowledgePanel({
  knowledgeGaps,
  rawOutcome,
  interpretedGoal,
  allGaps = [],
  knowledgeRequests: aiRequests,
  className,
}: MissionKnowledgePanelProps) {
  const gapIsCovered = useMissionKnowledgeStore((s) => s.gapIsCovered);
  const [docFormOpen, setDocFormOpen] = useState(false);
  const [docFormGap, setDocFormGap] = useState('');
  const [docFormTitle, setDocFormTitle] = useState('');

  const resolvedRequests = useMemo(
    () =>
      resolveKnowledgeRequests({
        gaps: knowledgeGaps,
        rawOutcome,
        knowledgeRequests: aiRequests,
      }),
    [knowledgeGaps, rawOutcome, aiRequests]
  );

  const uncovered = knowledgeGaps.filter((g) => !gapIsCovered(g, resolvedRequests));

  function quickStartForGap(gap: string) {
    setDocFormGap(gap);
    setDocFormTitle(gap.length > 48 ? `${gap.slice(0, 45)}…` : gap);
    setDocFormOpen(true);
    document.getElementById('mission-knowledge-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => document.getElementById('mission-doc-title')?.focus(), 300);
  }

  return (
    <section
      id="mission-knowledge-panel"
      className={cn(
        'rounded-lg border border-primary/25 bg-primary/[0.03] p-4 space-y-5 scroll-mt-24',
        className
      )}
      aria-label="Mission knowledge library"
    >
      <div className="flex items-start gap-2">
        <BookOpen className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0 space-y-1">
          <h3 className="text-sm font-semibold text-foreground">This Mission&apos;s knowledge library</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Scoped to this Mission only — not shared with other Missions or users. Same template for every
            outcome: AI-composed request fields plus documents you add (any title, any content).
            Vault secrets stay encrypted on device; domain docs sync to pgvector when configured.
          </p>
          <MissionScopeBadge />
        </div>
      </div>

      {knowledgeGaps.length > 0 && (
        <ul className="space-y-1.5">
          {knowledgeGaps.map((gap) => (
            <li
              key={gap}
              className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs rounded-md border border-border/60 bg-background/80 px-3 py-2"
            >
              <span className="min-w-0 flex-1 text-muted-foreground">{gap}</span>
              {gapIsCovered(gap, resolvedRequests) ? (
                <Badge variant="secondary" className="shrink-0 text-[10px]">
                  Covered
                </Badge>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="shrink-0 h-7 text-[11px]"
                  onClick={() => quickStartForGap(gap)}
                >
                  Add document
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {resolvedRequests.length > 0 && <MissionRequestPage requests={resolvedRequests} />}

      <MissionDocumentsLibrary
        knowledgeGaps={knowledgeGaps}
        formOpen={docFormOpen}
        onFormOpenChange={setDocFormOpen}
        initialLinkedGap={docFormGap}
        initialTitle={docFormTitle}
      />

      <MissionIntegrationDiscoveryPanel
        outcome={rawOutcome}
        interpretedGoal={interpretedGoal ?? rawOutcome}
        gaps={allGaps.length ? allGaps : knowledgeGaps}
      />

      {uncovered.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {uncovered.length} gap{uncovered.length === 1 ? '' : 's'} still open — complete forms or add documents
          before planning.
        </p>
      )}
    </section>
  );
}
